import { LoadState, useLoad } from "@redotech/react-util/load";
import { StyleRoot } from "@redotech/react-util/style";
import { MarketingEntryPoint } from "@redotech/redo-model/advanced-flow/schemas/marketing/marketing";
import { SignupForm as ISignupForm } from "@redotech/redo-model/signup-form-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { CustomFieldProvider } from "@redotech/redo-web/marketing/form-elements/context";
import { SignupForm } from "@redotech/redo-web/marketing/signup-form";
import {
  RedoMarketingFormLocalData,
  useSignupFormAnalytics,
} from "@redotech/redo-web/marketing/signup-form/popup-form-instance";
import {
  parseLocalData,
  submitFormStep,
} from "@redotech/redo-web/marketing/signup-form/utils";
import { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import { useCallback, useContext, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  RedoShopifyRpcClientContext,
  RedoShopifyRpcClientContextProvider,
} from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";
import { RedoRoot } from "../root";
import STYLE_MANAGER from "../style-manager";

const SIGNUP_LOCAL_STORAGE_KEY = "redo.marketing-signup-form";

export function initialize(autocheck: WidgetAutocheck) {
  log("Initializing Marketing embedded form");

  const elements = document.querySelectorAll(
    'div[id^="redo-marketing-embedded-form-"]',
  );

  const matchingElements = Array.from(elements).filter((element) => {
    return /^[a-zA-Z0-9]{5}$/.test(element.id.split("-").pop() ?? "");
  });

  log("Found Marketing embedded form container");

  if (matchingElements.length === 0 || !matchingElements[0]) {
    return;
  }

  // Shopify applies display: none to empty divs
  (matchingElements[0] as HTMLElement).style.display = "unset";

  const root = createRoot(matchingElements[0]);
  root.render(
    <StyleRoot container={matchingElements[0]} styleManager={STYLE_MANAGER}>
      <RedoRoot
        extensionShopifyClient={undefined}
        storeUrl={
          window.redoStorefront?.storeUrl ??
          window.Shopify?.shop ??
          window.Shopify?.Checkout?.apiHost
        }
      >
        <RedoShopifyRpcClientContextProvider
          baseUrl={
            window.redoStorefront?.env?.REDO_SHOPIFY_SERVER_URL ??
            process.env.REDO_SHOPIFY_SERVER_URL!
          }
          storeUrl={window.Shopify?.shop ?? window.Shopify?.Checkout?.apiHost}
          widgetId={window.redoWidgetId}
        >
          <RedoMarketingEmbeddedForm
            autocheck={autocheck}
            formDivId={matchingElements[0].id}
          />
        </RedoShopifyRpcClientContextProvider>
      </RedoRoot>
    </StyleRoot>,
  );
}

function useFormData(
  formsLoad: LoadState<
    Awaited<ReturnType<RedoShopifyRpcClient["getEmbeddedForms"]>>
  >,
  rpcClient: RedoShopifyRpcClient | undefined,
  customerHash: string | undefined,
  formDivId: string,
): { form: ISignupForm | null; formId: string | null } {
  const [form, setForm] = useState<ISignupForm | null>(null);
  const [formId, setFormId] = useState<string | null>(null);

  if (formsLoad.value && rpcClient && customerHash && !form) {
    const matchingForm = formsLoad.value.find(
      (f: (typeof formsLoad.value)[number]) =>
        f.form?.behavior.embeddedCode.includes(formDivId),
    );

    if (matchingForm) {
      log("Form found", matchingForm);
      void rpcClient
        .logSignupFormAnalytics({
          idempotencyKey: customerHash,
          formId: matchingForm.id,
          type: "view",
        })
        .catch((error) => {
          console.error("Error logging form view:", error);
        });

      setForm(matchingForm.form);
      setFormId(matchingForm.id);
    }
  }

  return { form, formId };
}

function RedoMarketingEmbeddedForm({
  autocheck,
  formDivId,
}: {
  autocheck: WidgetAutocheck;
  formDivId: string;
}) {
  const rpcClient = useContext(RedoShopifyRpcClientContext);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [localData, setLocalData] = useState<RedoMarketingFormLocalData>(() => {
    const stored = localStorage.getItem(SIGNUP_LOCAL_STORAGE_KEY);
    return stored ? parseLocalData(stored) : {};
  });

  const formsLoad = useLoad(async () => {
    if (!rpcClient) {
      return [];
    }

    const forms = await rpcClient.getEmbeddedForms({});
    return forms.filter((f: (typeof forms)[number]) => f.publishedAt);
  }, []);

  const { form, formId } = useFormData(
    formsLoad,
    rpcClient,
    localData.customerHash,
    formDivId,
  );

  const { logAnalytics } = useSignupFormAnalytics(
    rpcClient,
    formId,
    localData,
    setLocalData,
  );

  const submitStep = useCallback(
    async (i: number, formData: Record<string, string>) => {
      await submitFormStep({
        i,
        formData,
        form,
        formId,
        client: rpcClient,
        customerId,
        localData,
        setCustomerId,
        logAnalytics,
        setCurrentStep,
        entryPoint: MarketingEntryPoint.SIGNUP_FORM,
        skipPhoneSteps: true,
      });
    },
    [rpcClient, customerId, localData, form, formId, logAnalytics],
  );

  if (formsLoad.pending) {
    return null;
  }

  if (formsLoad.error) {
    return null;
  }

  if (!form) {
    return null;
  }

  if (rpcClient) {
    return (
      <CustomFieldProvider
        getCustomFieldOptions={(id) =>
          rpcClient.getCustomFieldOptions({ customFieldId: id })
        }
      >
        <SignupForm
          brandKit={autocheck.brandKit}
          currentStep={currentStep}
          form={form}
          onSubmitStep={submitStep}
        />
      </CustomFieldProvider>
    );
  }

  return (
    <SignupForm
      brandKit={autocheck.brandKit}
      currentStep={currentStep}
      form={form}
      onSubmitStep={submitStep}
    />
  );
}
